import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCPrintContainer, RCCRefModalProps, RCCSelectOptionsCard, RCCServicesApi, RCCSwitch, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import FadedDiv from "src/components/Animations/fade";
import ConfigsContainer from "src/components/ConfigsContainer";
import InputNumber from "src/components/InputNumber";
import Modal from "src/components/Modal";
import Select from "src/components/Select";
import Title from "src/components/Title";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import stringToNumber from "src/utils/stringToNumber";
import { IRefPrintContainer } from "react-common-components/dist/components/PrintContainer/interface";
import { IRefComponentSelectOptionsCard } from "react-common-components/dist/components/SelectOptionsCard/interface";
import ChartIndicatorsContainer from "../ChartIndicatorsContainer";
import mountDefaultValueChart from "../ChartIndicatorsContainer/mountDefaultValueChart";
import mountDefaultValueAccumulatedChart from "../ChartIndicatorsContainer/mountDefaultValuesAccumulated";
import ChartIndicatorsTitle from "../ChartIndicatorsTitle";
import { IChartConfigIndicators, IChartDataIndicators, IChartIndicators, IChartTableIndicators, IConfigsChartModal, IConfigsShowGoal, IFieldsChart, IModalIndicatorChart, IRefActionsModalChartIndicators } from "./interface";
import { PrintStylesAccumulatedValues } from "./styles";
import mountDefaultValueTable from "../ChartIndicatorsContainer/mountDefaultValueTable";

const ModalIndicatorChart: React.ForwardRefRenderFunction<IRefActionsModalChartIndicators, IModalIndicatorChart> = ({
	openValuesOfPeriodIndicator,
	onCloseCallback
}, ref) => {
	const { translate, currentLanguage } = useTranslation();
	const { setLoading } = useLoaderBackdrop();
	const { addToast } = RCCHookToast();

	// Modal Open Chart
	const modalOpenChart = useRef<RCCRefModalProps>(null);
	const [indicatorId, setIndicatorId] = useState(0);
	const [subtitle, setSubtitle] = useState<string>("");
	const [showAccumulatedValuesChartAndTable, setShowAccumulatedValuesChartAndTable] = useState(false);
	// Flag that marks if executes onClose
	const executeOnClose = useRef<boolean>(false);
	// Fullchart data
	const fullDataChartRef = useRef<IChartDataIndicators[]>([]);
	const fullDataChartAccumulatedRef = useRef<IChartDataIndicators[]>([]);
	// Charts data
	const [dataChart, setDataChart] = useState<IChartIndicators[]>([]);
	const [dataChartAccumulated, setDataChartAccumulated] = useState<IChartIndicators[]>([]);
	const [fieldsChart, setFieldsChart] = useState<IFieldsChart[]>([]);
	const [fieldsChartAccumulated, setFieldsChartAccumulated] = useState<IFieldsChart[]>([]);
	const [configCharts, setConfigCharts] = useState<IChartConfigIndicators>({} as IChartConfigIndicators);
	const [toggleConfigShowGoals, setToogleConfigShowGoals] = useState<boolean>(false);
	const [configShowGoals, setConfigShowGoals] = useState<IConfigsShowGoal>({
		showGoalImprovementInGraphic: undefined,
		showGoalCommitmentInGraphic: undefined,
		showGoalCompetitivenessInGraphic: undefined
	} as IConfigsShowGoal);
	// Table
	const [dataTable, setDataTable] = useState<IChartTableIndicators[]>([]);
	// Tendency
	const tendencyString = translate("Tendência");
	// Select
	const [selectYearOptions, setSelectYearOptions] = useState<{label: string, value: string}[]>([]);
	const [selectedYearOption, setSelectedYearOption] = useState<{label: string, value: string}[]>([]);
	const { control: controlYearsSelected, reset: resetYearsSelected} = useForm();
	// Show configs
	const [showConfigs, setShowConfigs] = useState(false);
	const showValuesRef = useRef<IRefComponentSelectOptionsCard>(null);
	const [configs, setConfigs] = useState<IConfigsChartModal>({ 
	} as IConfigsChartModal);
	const { handleSubmit : handleSubmitConfigs, control : controlConfigs, reset: resetConfigs, register: registerConfigs } = useForm();
	const submitConfigs = async (data: any) => {
		const obj = { 
			leftAxisYMin: stringToNumber(data.leftAxisYMin, currentLanguage),
			leftAxisYMax: stringToNumber(data.leftAxisYMax, currentLanguage),
			rightAxisYMin: stringToNumber(data.rightAxisYMin, currentLanguage),
			rightAxisYMax: stringToNumber(data.rightAxisYMax, currentLanguage),
			showValues: showValuesRef.current?.value
		};
		setConfigs(obj as IConfigsChartModal);
		setConfigCharts((prevState) => ({
			...prevState,
			iqc001Escalamin: obj.leftAxisYMin,
			iqc001Escalamax: obj.leftAxisYMax > 0 ? obj.leftAxisYMax : null,
			iqc001Escalamindir: obj.rightAxisYMin,
			iqc001Escalamaxdir: obj.rightAxisYMax > 0 ? obj.rightAxisYMax : null,
			showAll: obj.showValues === "1" ? undefined : obj.showValues === "3"
		}));
		changeConfigShowGoals(!toggleConfigShowGoals ? {
			showGoalCommitmentInGraphic: undefined,
			showGoalCompetitivenessInGraphic: undefined,
			showGoalImprovementInGraphic: undefined
		} : {
			showGoalCommitmentInGraphic: data.showGoalCommitmentInGraphic,
			showGoalCompetitivenessInGraphic: data.showGoalCompetitivenessInGraphic,
			showGoalImprovementInGraphic: data.showGoalImprovementInGraphic
		});
	};
	const changeConfigShowGoals = async (config: IConfigsShowGoal) => {
		if(config.showGoalCommitmentInGraphic === configShowGoals.showGoalCommitmentInGraphic &&
		   config.showGoalCompetitivenessInGraphic === configShowGoals.showGoalCompetitivenessInGraphic &&
		   config.showGoalImprovementInGraphic === configShowGoals.showGoalImprovementInGraphic
		)
			return;

		setLoading(true);

		const fullDataChart = fullDataChartRef.current.map((x) => ({
			name: x.name,
			hasFCA: x.hasFCA,
			values: x.values.map((val) => {
				let valShowInChart = val.showInChart;

				if(val.indicatorGoalType === "S" && config.showGoalImprovementInGraphic !== undefined)
					valShowInChart = config.showGoalImprovementInGraphic;
				else if(val.indicatorGoalType === "C" && config.showGoalCompetitivenessInGraphic !== undefined)
					valShowInChart = config.showGoalCompetitivenessInGraphic;
				else if(val.indicatorGoalType === "O" && config.showGoalCommitmentInGraphic !== undefined)
					valShowInChart = config.showGoalCommitmentInGraphic;	

				return{
					...val,
					showInChart: valShowInChart
				};
			})
		}));
		
		await defaultValueChart(fullDataChart);
				
		if(showAccumulatedValuesChartAndTable){
			const fullDataChartAccumulated = fullDataChartAccumulatedRef.current.map((x) => ({
				name: x.name,
				hasFCA: x.hasFCA,
				values: x.values.map((val) => {
					let valShowInChart = val.showInChart;

					if(val.indicatorGoalType === "S" && config.showGoalImprovementInGraphic !== undefined)
						valShowInChart = config.showGoalImprovementInGraphic;
					else if(val.indicatorGoalType === "C" && config.showGoalCompetitivenessInGraphic !== undefined)
						valShowInChart = config.showGoalCompetitivenessInGraphic;
					else if(val.indicatorGoalType === "O" && config.showGoalCommitmentInGraphic !== undefined)
						valShowInChart = config.showGoalCommitmentInGraphic;	

					return{
						...val,
						showInChart: valShowInChart
					};
				})
			}));
			
			await defaultValueChartAccumulated(fullDataChartAccumulated);
		}
		
		setConfigShowGoals(config);
		setLoading(false);
	};
	const resetConfigsFunction = () => {
		setConfigs({} as IConfigsChartModal);
		resetConfigs({ 
			leftAxisYMin: "",
			leftAxisYMax: "",
			rightAxisYMin: "",
			rightAxisYMax: "",
			startDate: null,
			endDate: null
		});
		showValuesRef.current?.changeValue("1");
		setToogleConfigShowGoals(false);
		setConfigShowGoals({
			showGoalCommitmentInGraphic: undefined,
			showGoalCompetitivenessInGraphic: undefined,
			showGoalImprovementInGraphic: undefined
		});
	};

	// Print
	const printRef = useRef<IRefPrintContainer>(null);
	const executePrint = () => {
		printRef.current?.print();
	};

	const defaultValueChart = async (fullDataChart: IChartDataIndicators[]) => {
		mountDefaultValueChart(fullDataChart, setFieldsChart, setDataChart, tendencyString);
	};
	const defaultValueTable = async (fullDataChart: IChartDataIndicators[], fullDataChartAccumulated: IChartDataIndicators[]) => {
		mountDefaultValueTable(fullDataChart, fullDataChartAccumulated, setDataTable, tendencyString);
	};
	const defaultValueChartAccumulated = async (fullDataChartAccumulated: IChartDataIndicators[]) => {
		mountDefaultValueAccumulatedChart(fullDataChartAccumulated, setFieldsChartAccumulated, setDataChartAccumulated, tendencyString);
	};
	const executeQueryToGetData = async (id: number, baseYear: { value: string, label: string }[], showAccumulatedValues: boolean, startDate?: Date | null, endDate?: Date | null) => {
		setDataChart([]);
		setDataChartAccumulated([]);
		setDataTable([]);
		
		const queryParamStartDate = startDate && startDate !== null ? formatDate(startDate, undefined, undefined, "/yyyy") : "01/01";
		const queryParamEndDate = endDate && endDate !== null ? formatDate(endDate, undefined, undefined, "/yyyy") : "31/12";
		const fullDataChart: IChartDataIndicators[] = [];
		const putYearOnLabelName = baseYear.length > 1;
		await Promise.all(baseYear.sort((a, b) => Number(a.value) - Number(b.value)).map(async (baseYearOpt) => {
			const year = baseYearOpt.value;
			const {data} = await RCCServicesApi.getRequest({
				method: "Indicators/ListIndicatorChartValues",
				urlParameters: {
					id,
					baseYear: putYearOnLabelName ? "" : year,
					startDate: `${queryParamStartDate}/${year}`,
					endDate: `${queryParamEndDate}/${year}`
				}
			});
			fullDataChart.push(...data.map((dt: any, index: number) => ({
				...dt,
				order: index
			})));
		}));
		fullDataChart.sort((a, b) =>  {
			if(a.order === undefined || b.order === undefined)
				return 0;
			return a.order - b.order;
		});
		fullDataChartRef.current = fullDataChart;

		await defaultValueChart(fullDataChart);
		
		const fullDataChartAccumulated: IChartDataIndicators[] = [];
		
		if(showAccumulatedValues){
			await Promise.all(baseYear.sort((a, b) => Number(a.value) - Number(b.value)).map(async (baseYearOpt) => {
				const year = baseYearOpt.value;
				const {data} = await RCCServicesApi.getRequest({
					method: "Indicators/ListIndicatorChartValuesAccumulated",
					urlParameters: {
						id,
						baseYear: year,
						startDate: `${queryParamStartDate}/${year}`,
						endDate: `${queryParamEndDate}/${year}`
					}
				});
				const dtChartAccumulated = data.filter((x: IChartDataIndicators) => fullDataChartAccumulated.findIndex((y) => y.name === x.name) === -1);
				if(dtChartAccumulated && dtChartAccumulated.length > 0)
					fullDataChartAccumulated.push(...dtChartAccumulated);
			}));
			fullDataChartAccumulatedRef.current = fullDataChartAccumulated.sort((a, b) => Number(b.name) - Number(a.name));

			await defaultValueChartAccumulated(fullDataChartAccumulated);
		}
		
		await defaultValueTable(fullDataChart, fullDataChartAccumulated);
	};
	const changeYearChart = async (e: { value: string, label: string}[]) => {
		if(e.length === 0)
			return;
		try{
			setLoading(true);
			setSelectedYearOption(e);
			await executeQueryToGetData(indicatorId, e, showAccumulatedValuesChartAndTable, configCharts.startDate, configCharts.endDate);
			setLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os valores dos indicadores"), addToast);
			setLoading(false);
		}
	};
	const actionButtonOpenChartIndicators = async (id: number, title: string, baseYear: string, showAccumulatedValues: boolean, startDate?: Date | null, endDate?: Date | null) => { 
		try{
			resetYearsSelected();
			setSubtitle(title);
			setLoading(true);
			setIndicatorId(id);
			setShowAccumulatedValuesChartAndTable(showAccumulatedValues);
			// Reset configs
			resetConfigsFunction();
			setShowConfigs(false);
			executeOnClose.current = false;

			const {data} = await RCCServicesApi.getRequest({
				method: "Indicators/GetIndicatorDetailsForChart",
				urlParameters: {
					id
				}
			});

			const conf = data as IChartConfigIndicators;
			setConfigCharts({
				...conf,
				startDate,
				endDate
			});

			setSelectedYearOption([{ value: baseYear, label: baseYear }]);
			setSelectYearOptions(conf.years.map((year: string) => ({
				label: year,
				value: year
			})));

			await executeQueryToGetData(id, [{ value: baseYear, label: baseYear }], showAccumulatedValues, startDate, endDate);

			setLoading(false);
			modalOpenChart.current?.openModal();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os valores dos indicadores"), addToast);
			setLoading(false);
		}
	};

	const onClickChartAreaBarLine = (periodId: number, isPresentation?: boolean) => openValuesOfPeriodIndicator(indicatorId, subtitle, periodId, () => {
		executeQueryToGetData(indicatorId, selectedYearOption, showAccumulatedValuesChartAndTable, configCharts.startDate, configCharts.endDate);
		executeOnClose.current = true;
	}, isPresentation);

	useImperativeHandle(ref, () => ({
		actionButtonOpenChartIndicators
	}));
    
	return (
		<>		
			{/* Modal CHART INDICATOR */}
			<Modal
				ref={modalOpenChart}
				header={{
					title: translate("Gráfico de indicador"),
					icon: {
						name: "chartBar"
					}
				}}
				footer= {{
					showSaveButton: false,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Salvar"),
					cancelButtonText: translate("Cancelar")
				}}
				onClose={() => {
					if(executeOnClose.current)
						onCloseCallback();
				}}
				styleInline="width: 95vw; .indicatorsChartContainer{ min-height: 400px; height: 60%; }"
				withoutForm
			>
				<RCCPrintContainer
					ref={printRef}
					styleInline={PrintStylesAccumulatedValues}
					documentTitle={subtitle}
				>
					<RCCGrid container item justifyContent="flex-end">
						<RCCGrid container item sm={12} md={4} alignItems="center" justifyContent="flex-end" className="noPrintElement">
							<RCCTooltip
								color={showConfigs || (
									configs.leftAxisYMax && configs.leftAxisYMin ||
										configs.rightAxisYMax && configs.rightAxisYMin
								) ? "blue" : "grey"}
								text={translate("Configurações")}
								position="left"
							>
								<RCCButton 
									color={showConfigs || (
										configs.leftAxisYMax && configs.leftAxisYMin ||
										configs.rightAxisYMax && configs.rightAxisYMin
									) ? "blue" : "grey"}
									icon={{
										name: "settings"
									}}
									onClick={() => setShowConfigs((prevState) => !prevState)}
								/>
							</RCCTooltip>
							<RCCTooltip
								color="grey"
								text={translate("Imprimir")}
								position="left"
							>
								<RCCButton 
									color="grey"
									icon={{
										name: "print"
									}}
									onClick={() => executePrint()}
								/>
							</RCCTooltip>
							<Select 
								control={controlYearsSelected}
								name="year"
								label=""
								defaultValue={selectedYearOption}
								options={selectYearOptions}
								isMulti
								onChangeCapture={(e: any) => changeYearChart(e)}
								styleInline={`
									flex: 1; 
									padding: 0; 
									margin: 0; 
									margin-top: -16px; 
									>div{     
										border-right: none;
										border-top: none;
										border-left: none;
										border-radius: 0;
									}
								`}
							/>
						</RCCGrid>
						<ConfigsContainer 
				 		showConfigs={showConfigs}
							onSubmitConfigs={handleSubmitConfigs(submitConfigs)}
							resetConfigs={resetConfigsFunction}
							className="noPrintElement"
						>
							<Title size="sm" color="purple">{translate("Escala")}</Title>
							<RCCGrid
								container
								item
								spacing={1}
							>
								<RCCGrid
									container 
									item
									xs={12}
									sm={6}
									md={6}
									lg={4}
								>
									<RCCGrid container item xs={6}>
										<InputNumber
											name="leftAxisYMin" 
											label={`${translate("Eixo Y esquerdo")} - ${translate("Min")}`}
											control={controlConfigs}
											defaultValue={configs.leftAxisYMin}
										/>
									</RCCGrid>
									<RCCGrid container item xs={6}>
										<InputNumber
											name="leftAxisYMax" 
											label={`${translate("Eixo Y esquerdo")} - ${translate("Max")}`}
											control={controlConfigs}
											defaultValue={configs.leftAxisYMax}
										/>
									</RCCGrid>
								</RCCGrid>

								<RCCGrid
									container 
									item
									xs={12}
									sm={6}
									md={6}
									lg={4}
								>
									<RCCGrid container item xs={6}>
										<InputNumber
											name="rightAxisYMin" 
											label={`${translate("Eixo Y direito")} - ${translate("Min")}`}
											control={controlConfigs}
											defaultValue={configs.rightAxisYMin}
										/>
									</RCCGrid>
									<RCCGrid container item xs={6}>
										<InputNumber
											name="rightAxisYMax" 
											label={`${translate("Eixo Y direito")} - ${translate("Max")}`}
											control={controlConfigs}
											defaultValue={configs.rightAxisYMax}
										/>
									</RCCGrid>
								</RCCGrid>

							</RCCGrid>
						
					    <Title size="sm" color="purple" styleInline="margin-top: 1rem;">{translate("Mostrar")}/{translate("Esconder")}</Title>
							<RCCGrid
								container
								item
								spacing={1}
							>
								<RCCGrid
									container 
									item
									xs={12}
									sm={6}
									md={6}
									lg={4}
								>
									<RCCSelectOptionsCard 
										defaultValue="1"
										options={[{
											value: "1",
											label: translate("Conforme indicador")
										},{
											value: "2",
											label: translate("Esconder todos")
										},{
											value: "3",
											label: translate("Mostrar todos")
										}]}
										label={translate("Mostrar valor no gráfico")}
										ref={showValuesRef}
									/>
								</RCCGrid>

								<RCCGrid
									container 
									item
									xs={12}
									sm={6}
									md={6}
									lg={4}
								>
									<RCCSwitch
										name="toggleConfigShowGoals"
										label={`${translate("Alterar metas que aparecem no gráfico")}`}
										register={registerConfigs}
										checked={toggleConfigShowGoals}
										onChange={() => setToogleConfigShowGoals((prevState) => !prevState)}
									/>							
								</RCCGrid>

							</RCCGrid>
							<RCCGrid container item>
								{toggleConfigShowGoals && (
									<FadedDiv styleInline=">label { flex-direction: row; } @media(min-width: 600px){ flex-direction: row; }">
										<RCCSwitch
											name="showGoalImprovementInGraphic"
											label={`${translate("Mostrar meta melhoria")}`}
											register={registerConfigs}
											defaultChecked
										/>
										<RCCSwitch
											name="showGoalCompetitivenessInGraphic"
											label={`${translate("Mostrar meta competitividade")}`}
											register={registerConfigs}
										/>
										<RCCSwitch
											name="showGoalCommitmentInGraphic"
											label={`${translate("Mostrar meta compromisso")}`}
											register={registerConfigs}
										/>
									</FadedDiv>
								)}
							</RCCGrid>
						</ConfigsContainer>
					</RCCGrid>

					<RCCGrid container item>
						<RCCGrid container alignItems="center" justifyContent="center" item sm={12} md={8}>
							<ChartIndicatorsTitle 
								title={subtitle}
								hideEvaluationForm={configCharts.iqc001Ocultarformaaval}
								evaluationForm={configCharts.iqc001Formaaval}
								descObjective={configCharts.iqc001Descobjetivo}
								method={configCharts.iqc001Metodo}
								measureUnit={configCharts.iqc001Taa009Descricao}
							/>
						</RCCGrid>
					</RCCGrid>

					<ChartIndicatorsContainer 
						configCharts={configCharts}
						dataChart={dataChart}
						dataChartAccumulated={dataChartAccumulated}
						fieldsChart={fieldsChart}
						fieldsChartAccumulated={fieldsChartAccumulated}
						onClickChartAreaBarLine={onClickChartAreaBarLine}
						dataTable={dataTable}
					/>
				</RCCPrintContainer>
			</Modal>
		</>
	);
};

export default forwardRef(ModalIndicatorChart);
